import { useMemo, useState, type ReactNode, type SyntheticEvent } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  DashboardOverview,
  DashboardRange,
  PaperOverview,
  PaperPositionDetail,
  PaperPositionSummary,
  PerformancePoint,
  ProcessHeartbeat,
  ShadowSimulation,
  TradeDetail,
  TradeSummary,
} from "../../src/dashboard/types.js";
import { ApiError, dashboardApi } from "./api.js";

type Tab = "overview" | "trades" | "simulations" | "operations";
const ranges: DashboardRange[] = ["24h", "7d", "30d", "90d", "all"];

export function App(): ReactNode {
  const client = useQueryClient();
  const session = useQuery({
    queryKey: ["session"],
    queryFn: dashboardApi.session,
  });
  if (session.isPending)
    return (
      <Centered>
        <Spinner label="Checking secure session" />
      </Centered>
    );
  if (session.error instanceof ApiError && session.error.status === 401)
    return <Login onSuccess={() => void client.invalidateQueries()} />;
  if (session.isError)
    return (
      <Centered>
        <ErrorState message={session.error.message} />
      </Centered>
    );
  return <Dashboard username={session.data.username} />;
}

function Login({ onSuccess }: { onSuccess: () => void }): ReactNode {
  const [username, setUsername] = useState("operator");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  async function submit(event: SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await dashboardApi.login(username, password);
      onSuccess();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Login failed");
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <main className="login-shell">
      <section className="login-card">
        <div className="brand-mark" aria-hidden="true">
          TE
        </div>
        <p className="eyebrow">Private operator surface</p>
        <h1>Trade Executor</h1>
        <p className="muted">
          Performance, position lifecycles, and execution health.
        </p>
        <form onSubmit={(event) => void submit(event)}>
          <label>
            Username
            <input
              value={username}
              autoComplete="username"
              onChange={(event) => {
                setUsername(event.target.value);
              }}
            />
          </label>
          <label>
            Password
            <input
              type="password"
              value={password}
              minLength={12}
              autoComplete="current-password"
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          </label>
          {error ? (
            <p className="form-error" role="alert">
              {error}
            </p>
          ) : null}
          <button
            className="primary"
            disabled={submitting || password.length < 12}
            type="submit"
          >
            {submitting ? "Signing in…" : "Open dashboard"}
          </button>
        </form>
      </section>
    </main>
  );
}

function Dashboard({ username }: { username: string }): ReactNode {
  const client = useQueryClient();
  const [tab, setTab] = useState<Tab>("overview");
  const [range, setRange] = useState<DashboardRange>("30d");
  async function logout(): Promise<void> {
    await dashboardApi.logout();
    client.clear();
    window.location.assign("/bot/");
  }
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark small">TE</span>
          <span>Trade Executor</span>
        </div>
        <nav aria-label="Dashboard sections">
          {(["overview", "trades", "simulations", "operations"] as const).map(
            (item) => (
              <button
                key={item}
                className={tab === item ? "active" : ""}
                onClick={() => {
                  setTab(item);
                }}
              >
                <NavIcon tab={item} /> <span>{tabLabel(item)}</span>
              </button>
            ),
          )}
        </nav>
        <div className="sidebar-foot">
          <span className="status-dot" />
          Private HTTPS
          <br />
          <small>{username}</small>
        </div>
      </aside>
      <main className="content">
        <header className="topbar">
          <div>
            <p className="eyebrow">Bot-managed capital only</p>
            <h1>{tabLabel(tab)}</h1>
          </div>
          <div className="topbar-actions">
            {tab === "overview" || tab === "simulations" ? (
              <RangePicker value={range} onChange={setRange} />
            ) : null}
            <button className="ghost" onClick={() => void logout()}>
              Log out
            </button>
          </div>
        </header>
        {tab === "overview" ? <Overview range={range} /> : null}
        {tab === "trades" ? <Trades /> : null}
        {tab === "simulations" ? <Simulations range={range} /> : null}
        {tab === "operations" ? <Operations /> : null}
      </main>
    </div>
  );
}

function Overview({ range }: { range: DashboardRange }): ReactNode {
  const overview = useQuery({
    queryKey: ["overview", range],
    queryFn: () => dashboardApi.overview(range),
    refetchInterval: 10_000,
  });
  const performance = useQuery({
    queryKey: ["performance", range],
    queryFn: () => dashboardApi.performance(range),
    refetchInterval: 30_000,
  });
  if (overview.isPending) return <Spinner label="Loading performance" />;
  if (overview.isError) return <ErrorState message={overview.error.message} />;
  const data = overview.data;
  return (
    <div className="stack">
      <StatusStrip data={data} />
      <section className="kpi-grid" aria-label="Performance summary">
        <Kpi
          label="Total P&L"
          value={money(data.performance.total_pnl_usd)}
          detail="Realized + executable open value"
          unavailable={data.performance.total_pnl_usd === null}
        />
        <Kpi
          label="Realized P&L"
          value={money(data.performance.realized_pnl_usd)}
          detail={`${data.counts.closed_positions} closed positions`}
        />
        <Kpi
          label="Unrealized P&L"
          value={money(data.performance.unrealized_pnl_usd)}
          detail={freshness(data.freshness.latest_valuation_at)}
          unavailable={data.performance.unrealized_pnl_usd === null}
        />
        <Kpi
          label="ROI"
          value={percent(data.performance.roi_percent)}
          detail={`${money(data.performance.entry_cost_usd)} deployed`}
          unavailable={data.performance.roi_percent === null}
        />
        <Kpi
          label="Win rate"
          value={percent(data.performance.win_rate_percent)}
          detail="Closed positions only"
          unavailable={data.performance.win_rate_percent === null}
        />
        <Kpi
          label="Open exposure"
          value={money(data.performance.exposure_usd)}
          detail={`${data.counts.open_positions} open positions`}
          unavailable={data.performance.exposure_usd === null}
        />
      </section>
      {data.alerts.length ? (
        <section className="alert-list" aria-label="Operational alerts">
          {data.alerts.map((alert) => (
            <div className={`alert ${alert.severity}`} key={alert.id}>
              <strong>{alert.title}</strong>
              <span>{alert.detail}</span>
            </div>
          ))}
        </section>
      ) : null}
      <section className="chart-grid">
        <Card
          title="Portfolio P&L"
          subtitle={`${rangeLabel(range)} · USD · verified bot positions`}
          wide
        >
          {performance.data && performance.data.items.length >= 8 ? (
            <PnlChart points={performance.data.items} />
          ) : (
            <EmptyChart
              title="Trend begins after eight snapshots"
              detail="The monitor stores a portfolio point every five minutes once executable valuations are available."
            />
          )}
        </Card>
        <Card
          title="Execution accounting"
          subtitle="Confirmed fills by accounting confidence"
        >
          <AccountingBars data={data} />
        </Card>
      </section>
      <section className="lower-grid">
        <Card
          title="Position coverage"
          subtitle="Current executable valuation availability"
        >
          <Coverage data={data} />
        </Card>
        <Card title="Trading state" subtitle="Read-only runtime controls">
          <RuntimeState data={data} />
        </Card>
      </section>
    </div>
  );
}

function Trades(): ReactNode {
  const [state, setState] = useState("");
  const [mint, setMint] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const positions = useQuery({
    queryKey: ["positions", state, mint],
    queryFn: () => dashboardApi.positions({ state, mint }),
    refetchInterval: 10_000,
  });
  return (
    <div className="stack">
      <section className="filters">
        <label>
          State
          <select
            value={state}
            onChange={(event) => {
              setState(event.target.value);
            }}
          >
            <option value="">All states</option>
            {[
              "OPEN_PRE_TP",
              "TP1_REACHED",
              "TP2_REACHED",
              "MOONBAG_HELD",
              "CLOSED_STOP",
              "CLOSED_TRAILING_STOP",
              "CLOSED_MANUAL",
            ].map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        <label>
          Mint
          <input
            placeholder="Exact Solana mint"
            value={mint}
            onChange={(event) => {
              setMint(event.target.value.trim());
            }}
          />
        </label>
      </section>
      <Card
        title="Position lifecycles"
        subtitle="One entry with every confirmed exit and strategy transition"
        wide
      >
        {positions.isPending ? (
          <Spinner label="Loading trades" />
        ) : positions.isError ? (
          <ErrorState message={positions.error.message} />
        ) : (
          <TradesTable items={positions.data.items} onSelect={setSelected} />
        )}
      </Card>
      {selected ? (
        <TradeDrawer
          id={selected}
          onClose={() => {
            setSelected(null);
          }}
        />
      ) : null}
    </div>
  );
}

function TradesTable({
  items,
  onSelect,
}: {
  items: TradeSummary[];
  onSelect: (id: string) => void;
}): ReactNode {
  const columns = useMemo<ColumnDef<TradeSummary>[]>(
    () => [
      { header: "Token", cell: ({ row }) => <Token position={row.original} /> },
      {
        header: "State",
        accessorKey: "state",
        cell: ({ getValue }) => <Badge value={String(getValue())} />,
      },
      {
        header: "Entry",
        accessorKey: "entry_cost_usd",
        cell: ({ getValue }) => money(String(getValue())),
      },
      {
        header: "P&L",
        accessorKey: "total_pnl_usd",
        cell: ({ getValue }) => (
          <Signed value={getValue() === null ? null : String(getValue())} />
        ),
      },
      {
        header: "ROI",
        accessorKey: "roi_percent",
        cell: ({ getValue }) =>
          percent(getValue() === null ? null : String(getValue())),
      },
      {
        header: "Opened",
        accessorKey: "created_at",
        cell: ({ getValue }) => shortDate(String(getValue())),
      },
    ],
    [],
  );
  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });
  if (!items.length)
    return (
      <EmptyChart
        title="No confirmed positions"
        detail="Confirmed bot-managed entries will appear here. Shadow quotes remain in Simulations."
      />
    );
  return (
    <div className="table-wrap">
      <table>
        <thead>
          {table.getHeaderGroups().map((group) => (
            <tr key={group.id}>
              {group.headers.map((header) => (
                <th key={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              tabIndex={0}
              onClick={() => {
                onSelect(row.original.id);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") onSelect(row.original.id);
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TradeDrawer({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}): ReactNode {
  const trade = useQuery({
    queryKey: ["position", id],
    queryFn: () => dashboardApi.position(id),
  });
  return (
    <div className="drawer-backdrop" onMouseDown={onClose}>
      <aside
        className="drawer"
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
      >
        <button
          className="drawer-close"
          onClick={onClose}
          aria-label="Close trade details"
        >
          ×
        </button>
        {trade.isPending ? (
          <Spinner label="Loading lifecycle" />
        ) : trade.isError ? (
          <ErrorState message={trade.error.message} />
        ) : (
          <TradeDetails data={trade.data} />
        )}
      </aside>
    </div>
  );
}

function TradeDetails({ data }: { data: TradeDetail }): ReactNode {
  return (
    <div className="stack compact">
      <div>
        <p className="eyebrow">Position lifecycle</p>
        <h2>{data.symbol ?? truncateMint(data.mint)}</h2>
        <p className="mono muted">{data.mint}</p>
      </div>
      <div className="detail-grid">
        <Detail label="Entry cost" value={money(data.entry_cost_usd)} />
        <Detail label="Total P&L" value={money(data.total_pnl_usd)} />
        <Detail
          label="Realized proceeds"
          value={money(data.realized_proceeds_usd)}
        />
        <Detail
          label="High-water"
          value={`${decimal(data.high_water_multiple)}×`}
        />
        <Detail
          label="Active stop"
          value={
            data.active_stop_multiple
              ? `${decimal(data.active_stop_multiple)}×`
              : "Not active"
          }
        />
        <Detail
          label="Last valuation"
          value={freshness(data.valuation_observed_at)}
        />
      </div>
      <h3>Allocations</h3>
      <div className="allocation-grid">
        {data.allocations.map((item) => (
          <div key={item.allocation_type}>
            <strong>{item.allocation_type}</strong>
            <span>{item.state}</span>
            <small>
              {tokenAmount(item.sold_quantity, data.token_decimals)} /{" "}
              {tokenAmount(item.original_quantity, data.token_decimals)}
            </small>
          </div>
        ))}
      </div>
      <h3>Confirmed executions</h3>
      {data.fills.length ? (
        <div className="timeline">
          {data.fills.map((fill) => (
            <div key={fill.id}>
              <span className="timeline-dot" />
              <div>
                <strong>
                  {fill.action.toUpperCase()} · {money(fill.gross_value_usd)}
                </strong>
                <p>
                  {shortDate(fill.confirmed_at)} · {fill.router} ·{" "}
                  <Badge value={fill.accounting_status} />
                </p>
                {fill.transaction_signature ? (
                  <a
                    href={`https://solscan.io/tx/${fill.transaction_signature}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    View on Solscan ↗
                  </a>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="muted">No fill records.</p>
      )}
      <h3>Strategy timeline</h3>
      <div className="timeline">
        {data.timeline.map((event, index) => (
          <div key={`${event.created_at}-${index}`}>
            <span className="timeline-dot open" />
            <div>
              <strong>{event.to_state}</strong>
              <p>
                {shortDate(event.created_at)}
                {event.valuation_multiple
                  ? ` · ${decimal(event.valuation_multiple)}×`
                  : ""}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Simulations({ range }: { range: DashboardRange }): ReactNode {
  const [state, setState] = useState("");
  const [mint, setMint] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const overview = useQuery({
    queryKey: ["paper-overview"],
    queryFn: dashboardApi.paperOverview,
    refetchInterval: 10_000,
  });
  const performance = useQuery({
    queryKey: ["paper-performance", range],
    queryFn: () => dashboardApi.paperPerformance(range),
    refetchInterval: 30_000,
  });
  const positions = useQuery({
    queryKey: ["paper-positions", state, mint],
    queryFn: () => dashboardApi.paperPositions({ state, mint }),
    refetchInterval: 10_000,
  });
  const diagnostics = useQuery({
    queryKey: ["simulations"],
    queryFn: dashboardApi.simulations,
    refetchInterval: 10_000,
  });
  if (overview.isPending) return <Spinner label="Loading paper portfolio" />;
  if (overview.isError) return <ErrorState message={overview.error.message} />;
  const data = overview.data;
  return (
    <div className="stack">
      <div className="simulation-banner">
        <strong>Paper trading — no funds or transactions</strong>
        <span>
          Results use expected Jupiter quote output without network fees or
          landing risk. They remain isolated from live accounting.
        </span>
      </div>
      <PaperStatus data={data} />
      <section className="kpi-grid" aria-label="Paper performance summary">
        <Kpi
          label="Paper P&L"
          value={money(data.performance.total_pnl_usd)}
          detail="Realized + executable open value"
          unavailable={data.performance.total_pnl_usd === null}
        />
        <Kpi
          label="Realized P&L"
          value={money(data.performance.realized_pnl_usd)}
          detail={`${data.counts.closed_positions} closed paper trades`}
        />
        <Kpi
          label="Unrealized P&L"
          value={money(data.performance.unrealized_pnl_usd)}
          detail={freshness(data.freshness.latest_valuation_at)}
          unavailable={data.performance.unrealized_pnl_usd === null}
        />
        <Kpi
          label="Paper ROI"
          value={percent(data.performance.roi_percent)}
          detail={`${money(data.performance.entry_cost_usd)} simulated`}
          unavailable={data.performance.roi_percent === null}
        />
        <Kpi
          label="Win rate"
          value={percent(data.performance.win_rate_percent)}
          detail="Closed paper trades only"
          unavailable={data.performance.win_rate_percent === null}
        />
        <Kpi
          label="Open exposure"
          value={money(data.performance.exposure_usd)}
          detail={`${data.counts.open_positions} open paper trades`}
          unavailable={data.performance.exposure_usd === null}
        />
      </section>
      <section className="chart-grid">
        <Card
          title="Paper P&L trend"
          subtitle={`${rangeLabel(range)} · quote-backed portfolio snapshots`}
          wide
        >
          {performance.data && performance.data.items.length >= 8 ? (
            <PnlChart points={performance.data.items} />
          ) : (
            <EmptyChart
              title="Trend begins after eight snapshots"
              detail="Paper positions are revalued from executable sell quotes every monitor cycle."
            />
          )}
        </Card>
        <Card title="Valuation coverage" subtitle="Open paper positions only">
          <PaperCoverage data={data} />
        </Card>
      </section>
      <section className="filters">
        <label>
          State
          <select
            value={state}
            onChange={(event) => {
              setState(event.target.value);
            }}
          >
            <option value="">All states</option>
            {[
              "OPEN_PRE_TP",
              "TP1_REACHED",
              "TP2_REACHED",
              "MOONBAG_HELD",
              "CLOSED_STOP",
              "CLOSED_TRAILING_STOP",
              "CLOSED_MANUAL",
            ].map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        <label>
          Mint
          <input
            placeholder="Exact Solana mint"
            value={mint}
            onChange={(event) => {
              setMint(event.target.value.trim());
            }}
          />
        </label>
      </section>
      <Card
        title="Paper trade lifecycles"
        subtitle="Entry, strategy allocations, simulated exits, and quote-based P&L"
        wide
      >
        {positions.isPending ? (
          <Spinner label="Loading paper trades" />
        ) : positions.isError ? (
          <ErrorState message={positions.error.message} />
        ) : (
          <PaperPositionsTable
            items={positions.data.items}
            onSelect={setSelected}
          />
        )}
      </Card>
      <Card
        title="Quote diagnostics"
        subtitle="Raw shadow quotes used to open paper trades"
        wide
      >
        {diagnostics.isPending ? (
          <Spinner label="Loading simulations" />
        ) : diagnostics.isError ? (
          <ErrorState message={diagnostics.error.message} />
        ) : (
          <SimulationTable items={diagnostics.data.items} />
        )}
      </Card>
      {selected ? (
        <PaperDrawer
          id={selected}
          onClose={() => {
            setSelected(null);
          }}
        />
      ) : null}
    </div>
  );
}

function PaperStatus({ data }: { data: PaperOverview }): ReactNode {
  return (
    <section className="status-strip">
      <div>
        <span
          className={`status-dot ${data.runtime.paper_enabled ? "live" : ""}`}
        />
        <strong>{data.runtime.paper_enabled ? "RUNNING" : "STOPPED"}</strong>
        <small>Paper monitor</small>
      </div>
      <div>
        <strong>{data.counts.positions}</strong>
        <small>Paper trades</small>
      </div>
      <div>
        <strong>{data.counts.simulated_fills}</strong>
        <small>Simulated fills</small>
      </div>
      <div>
        <strong>{freshness(data.generated_at)}</strong>
        <small>Dashboard refreshed</small>
      </div>
    </section>
  );
}

function PaperCoverage({ data }: { data: PaperOverview }): ReactNode {
  const total = data.counts.open_positions;
  const covered = data.freshness.valuation_coverage;
  const ratio = total ? Math.round((covered / total) * 100) : null;
  return (
    <div className="coverage">
      <div
        className="coverage-ring"
        style={
          { "--coverage": `${(ratio ?? 0) * 3.6}deg` } as React.CSSProperties
        }
      >
        <strong>{ratio === null ? "N/A" : `${ratio}%`}</strong>
      </div>
      <div>
        <strong>
          {covered} of {total}
        </strong>
        <span>open paper positions valued</span>
        <small>
          Missing quotes make aggregate P&L unavailable, never zero.
        </small>
      </div>
    </div>
  );
}

function PaperPositionsTable({
  items,
  onSelect,
}: {
  items: PaperPositionSummary[];
  onSelect: (id: string) => void;
}): ReactNode {
  if (!items.length)
    return (
      <EmptyChart
        title="No paper trades yet"
        detail="Fresh Nones buy signals will open paper lifecycles while shadow mode is running."
      />
    );
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Token</th>
            <th>State</th>
            <th>Entry</th>
            <th>P&amp;L</th>
            <th>ROI</th>
            <th>Opened</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr
              key={item.id}
              tabIndex={0}
              onClick={() => {
                onSelect(item.id);
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") onSelect(item.id);
              }}
            >
              <td>
                <Token position={item} />
              </td>
              <td>
                <Badge value={item.state} />
              </td>
              <td>{money(item.entry_cost_usd)}</td>
              <td>
                <Signed value={item.total_pnl_usd} />
              </td>
              <td>{percent(item.roi_percent)}</td>
              <td>{shortDate(item.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaperDrawer({
  id,
  onClose,
}: {
  id: string;
  onClose: () => void;
}): ReactNode {
  const trade = useQuery({
    queryKey: ["paper-position", id],
    queryFn: () => dashboardApi.paperPosition(id),
  });
  return (
    <div className="drawer-backdrop" onMouseDown={onClose}>
      <aside
        className="drawer"
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
      >
        <button
          className="drawer-close"
          onClick={onClose}
          aria-label="Close paper trade details"
        >
          ×
        </button>
        {trade.isPending ? (
          <Spinner label="Loading paper lifecycle" />
        ) : trade.isError ? (
          <ErrorState message={trade.error.message} />
        ) : (
          <PaperTradeDetails data={trade.data} />
        )}
      </aside>
    </div>
  );
}

function PaperTradeDetails({ data }: { data: PaperPositionDetail }): ReactNode {
  return (
    <div className="stack compact">
      <div>
        <p className="eyebrow">Paper position lifecycle</p>
        <h2>{truncateMint(data.mint)}</h2>
        <p className="mono muted">{data.mint}</p>
      </div>
      <div className="simulation-banner">
        <strong>No on-chain execution</strong>
        <span>
          Expected Jupiter quote output; network fees and landing risk excluded.
        </span>
      </div>
      <div className="detail-grid">
        <Detail label="Entry cost" value={money(data.entry_cost_usd)} />
        <Detail label="Total P&L" value={money(data.total_pnl_usd)} />
        <Detail
          label="Realized proceeds"
          value={money(data.realized_proceeds_usd)}
        />
        <Detail
          label="High-water"
          value={`${decimal(data.high_water_multiple)}×`}
        />
        <Detail
          label="Active stop"
          value={
            data.active_stop_multiple
              ? `${decimal(data.active_stop_multiple)}×`
              : "Not active"
          }
        />
        <Detail
          label="Last valuation"
          value={freshness(data.valuation_observed_at)}
        />
      </div>
      <h3>Strategy allocations</h3>
      <div className="allocation-grid">
        {data.allocations.map((item) => (
          <div key={item.allocation_type}>
            <strong>{item.allocation_type}</strong>
            <span>{item.state}</span>
            <small>
              {item.sold_quantity} / {item.original_quantity} base units
            </small>
          </div>
        ))}
      </div>
      <h3>Simulated fills</h3>
      <div className="timeline">
        {data.fills.map((fill) => (
          <div key={fill.id}>
            <span className="timeline-dot" />
            <div>
              <strong>
                {fill.action_type} · {money(fill.gross_value_usd)}
              </strong>
              <p>
                {shortDate(fill.filled_at)} · {fill.router} · expected quote
              </p>
            </div>
          </div>
        ))}
      </div>
      <h3>Strategy timeline</h3>
      <div className="timeline">
        {data.timeline.map((event, index) => (
          <div key={`${event.created_at}-${index}`}>
            <span className="timeline-dot open" />
            <div>
              <strong>{event.to_state}</strong>
              <p>
                {shortDate(event.created_at)}
                {event.action_type ? ` · ${event.action_type}` : ""}
                {event.valuation_multiple
                  ? ` · ${decimal(event.valuation_multiple)}×`
                  : ""}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SimulationTable({ items }: { items: ShadowSimulation[] }): ReactNode {
  if (!items.length)
    return (
      <EmptyChart
        title="No shadow simulations"
        detail="Shadow-mode quote activity will appear here without affecting actual performance."
      />
    );
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>Token</th>
            <th>Action</th>
            <th>Value</th>
            <th>Route</th>
            <th>Slippage</th>
            <th>Time</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td className="mono">{truncateMint(item.mint)}</td>
              <td>{item.action.toUpperCase()}</td>
              <td>{money(item.output_value_usd)}</td>
              <td>{item.router}</td>
              <td>{item.slippage_bps} bps</td>
              <td>{shortDate(item.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Operations(): ReactNode {
  const query = useQuery({
    queryKey: ["operations"],
    queryFn: dashboardApi.operations,
    refetchInterval: 10_000,
  });
  return (
    <div className="stack">
      <Card
        title="Process heartbeats"
        subtitle="A process is stale after 30 seconds without a database heartbeat"
        wide
      >
        {query.isPending ? (
          <Spinner label="Loading process status" />
        ) : query.isError ? (
          <ErrorState message={query.error.message} />
        ) : (
          <HeartbeatGrid items={query.data.heartbeats} />
        )}
      </Card>
      <Card
        title="Safety posture"
        subtitle="This dashboard cannot mutate execution state"
        wide
      >
        <div className="safety-grid">
          <Detail label="Dashboard access" value="Dedicated secure session" />
          <Detail label="Database access" value="Curated read models" />
          <Detail label="Trading controls" value="Not exposed" />
          <Detail label="Private key material" value="Never available" />
        </div>
      </Card>
    </div>
  );
}

function PnlChart({ points }: { points: PerformancePoint[] }): ReactNode {
  const rows = points.map((point) => ({
    ...point,
    label: shortTime(point.bucket_at),
    realized: Number(point.realized_pnl_usd),
    total: Number(point.total_pnl_usd),
  }));
  return (
    <div className="chart">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart
          data={rows}
          margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
        >
          <defs>
            <linearGradient id="totalFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3157d5" stopOpacity={0.24} />
              <stop offset="100%" stopColor="#3157d5" stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid vertical={false} stroke="#e7eaf0" />
          <XAxis
            dataKey="label"
            tickLine={false}
            axisLine={false}
            minTickGap={36}
          />
          <YAxis
            tickFormatter={(value: number) => `$${value}`}
            tickLine={false}
            axisLine={false}
            width={56}
          />
          <Tooltip formatter={(value) => money(String(value))} />
          <ReferenceLine y={0} stroke="#8b93a7" />
          <Area
            type="monotone"
            dataKey="total"
            name="Total P&L"
            stroke="#3157d5"
            fill="url(#totalFill)"
            strokeWidth={2}
          />
          <Area
            type="monotone"
            dataKey="realized"
            name="Realized P&L"
            stroke="#b27b13"
            fill="transparent"
            strokeDasharray="5 4"
            strokeWidth={2}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function AccountingBars({ data }: { data: DashboardOverview }): ReactNode {
  const rows = [
    { name: "Verified", value: data.counts.verified_fills },
    { name: "Provisional", value: data.counts.provisional_fills },
    { name: "Failed", value: data.counts.failed_orders },
  ];
  return (
    <div className="chart small-chart">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={rows} layout="vertical" margin={{ left: 8, right: 18 }}>
          <CartesianGrid horizontal={false} stroke="#e7eaf0" />
          <XAxis
            type="number"
            allowDecimals={false}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="name"
            axisLine={false}
            tickLine={false}
            width={82}
          />
          <Tooltip />
          <Bar dataKey="value" fill="#3157d5" radius={[0, 5, 5, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function Coverage({ data }: { data: DashboardOverview }): ReactNode {
  const total = data.counts.open_positions;
  const covered = data.freshness.valuation_coverage;
  const ratio = total ? Math.round((covered / total) * 100) : null;
  return (
    <div className="coverage">
      <div
        className="coverage-ring"
        style={
          {
            "--coverage": `${(ratio ?? 0) * 3.6}deg`,
          } as React.CSSProperties
        }
      >
        <strong>{ratio === null ? "N/A" : `${ratio}%`}</strong>
      </div>
      <div>
        <strong>
          {covered} of {total}
        </strong>
        <span>open positions valued</span>
        <small>
          {total
            ? "Missing quotes are never shown as zero."
            : "No open exposure."}
        </small>
      </div>
    </div>
  );
}

function RuntimeState({ data }: { data: DashboardOverview }): ReactNode {
  return (
    <div className="runtime-list">
      <StateRow label="Execution mode" value={data.runtime.mode} />
      <StateRow
        label="Global pause"
        value={data.runtime.paused ? "paused" : "not paused"}
      />
      <StateRow
        label="Buying"
        value={data.runtime.buying_enabled ? "enabled" : "disabled"}
      />
      <StateRow
        label="Selling"
        value={data.runtime.selling_enabled ? "enabled" : "disabled"}
      />
      <StateRow
        label="Monitoring"
        value={data.runtime.monitoring_enabled ? "enabled" : "disabled"}
      />
    </div>
  );
}
function HeartbeatGrid({ items }: { items: ProcessHeartbeat[] }): ReactNode {
  if (!items.length)
    return (
      <EmptyChart
        title="No heartbeats yet"
        detail="Process status appears after the upgraded services start."
      />
    );
  return (
    <div className="heartbeat-grid">
      {items.map((item) => (
        <div key={item.role} className="heartbeat">
          <span className={`status-dot ${item.stale ? "stale" : ""}`} />
          <strong>{capitalize(item.role)}</strong>
          <Badge value={item.stale ? "STALE" : item.status.toUpperCase()} />
          <small>{freshness(item.last_seen_at)}</small>
          <code>{item.release_sha?.slice(0, 8) ?? "release n/a"}</code>
        </div>
      ))}
    </div>
  );
}
function StatusStrip({ data }: { data: DashboardOverview }): ReactNode {
  return (
    <section className="status-strip">
      <div>
        <span
          className={`status-dot ${data.runtime.mode === "live" ? "live" : ""}`}
        />
        <strong>{data.runtime.mode.toUpperCase()}</strong>
        <small>Execution mode</small>
      </div>
      <div>
        <strong>{data.counts.queue_depth}</strong>
        <small>Queued orders</small>
      </div>
      <div>
        <strong>{data.counts.reconciliation_backlog}</strong>
        <small>Reconciliation</small>
      </div>
      <div>
        <strong>{freshness(data.generated_at)}</strong>
        <small>Dashboard refreshed</small>
      </div>
    </section>
  );
}
function Kpi({
  label,
  value,
  detail,
  unavailable = false,
}: {
  label: string;
  value: string;
  detail: string;
  unavailable?: boolean;
}): ReactNode {
  return (
    <article className={`kpi ${unavailable ? "unavailable" : ""}`}>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}
function Card({
  title,
  subtitle,
  wide = false,
  children,
}: {
  title: string;
  subtitle: string;
  wide?: boolean;
  children: ReactNode;
}): ReactNode {
  return (
    <section className={`card ${wide ? "wide" : ""}`}>
      <header>
        <div>
          <h2>{title}</h2>
          <p>{subtitle}</p>
        </div>
      </header>
      {children}
    </section>
  );
}
function EmptyChart({
  title,
  detail,
}: {
  title: string;
  detail: string;
}): ReactNode {
  return (
    <div className="empty">
      <div className="empty-icon">⌁</div>
      <strong>{title}</strong>
      <span>{detail}</span>
    </div>
  );
}
function Spinner({ label }: { label: string }): ReactNode {
  return (
    <div className="spinner-wrap">
      <span className="spinner" />
      <span>{label}</span>
    </div>
  );
}
function ErrorState({ message }: { message: string }): ReactNode {
  return (
    <div className="empty error-state">
      <strong>Dashboard unavailable</strong>
      <span>{message}</span>
      <button
        className="ghost"
        onClick={() => {
          window.location.reload();
        }}
      >
        Retry
      </button>
    </div>
  );
}
function Centered({ children }: { children: ReactNode }): ReactNode {
  return <main className="centered">{children}</main>;
}
function Detail({ label, value }: { label: string; value: string }): ReactNode {
  return (
    <div className="detail">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
function StateRow({
  label,
  value,
}: {
  label: string;
  value: string;
}): ReactNode {
  return (
    <div>
      <span>{label}</span>
      <Badge value={value.toUpperCase()} />
    </div>
  );
}
function Token({
  position,
}: {
  position: {
    mint: string;
    source: string;
    symbol?: string | null;
    name?: string | null;
  };
}): ReactNode {
  return (
    <div className="token">
      <span className="token-avatar">
        {position.symbol?.slice(0, 2) ?? "◎"}
      </span>
      <div>
        <strong>{position.symbol ?? truncateMint(position.mint)}</strong>
        <small>{position.name ?? position.source}</small>
      </div>
    </div>
  );
}
function Badge({ value }: { value: string }): ReactNode {
  const tone = /FAILED|REJECT|STALE|CRITICAL/.test(value)
    ? "danger"
    : /LIVE|VERIFIED|RUNNING|ENABLED|REACHED|HELD/.test(value)
      ? "strong"
      : "neutral";
  return <span className={`badge ${tone}`}>{value.replaceAll("_", " ")}</span>;
}
function Signed({ value }: { value: string | null }): ReactNode {
  if (value === null) return <span className="muted">Unavailable</span>;
  const number = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
    signDisplay: "always",
  }).format(Number(value));
  return (
    <span className={Number(value) < 0 ? "signed negative" : "signed"}>
      {number}
    </span>
  );
}
function RangePicker({
  value,
  onChange,
}: {
  value: DashboardRange;
  onChange: (range: DashboardRange) => void;
}): ReactNode {
  return (
    <div className="range-picker" aria-label="Performance range">
      {ranges.map((range) => (
        <button
          key={range}
          className={range === value ? "active" : ""}
          onClick={() => {
            onChange(range);
          }}
        >
          {range.toUpperCase()}
        </button>
      ))}
    </div>
  );
}
function NavIcon({ tab }: { tab: Tab }): ReactNode {
  const glyph =
    tab === "overview"
      ? "⌁"
      : tab === "trades"
        ? "⇄"
        : tab === "simulations"
          ? "◇"
          : "⊙";
  return <span aria-hidden="true">{glyph}</span>;
}

function money(value: string | null): string {
  return value === null
    ? "Unavailable"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
      }).format(Number(value));
}
function percent(value: string | null): string {
  return value === null
    ? "N/A"
    : `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(Number(value))}%`;
}
function decimal(value: string): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 3 }).format(
    Number(value),
  );
}
function tokenAmount(value: string, decimals: number): string {
  const padded = value.padStart(decimals + 1, "0");
  const whole = padded.slice(0, -decimals) || "0";
  const fraction = decimals ? padded.slice(-decimals).replace(/0+$/, "") : "";
  return fraction ? `${whole}.${fraction}` : whole;
}
function shortDate(value: string): string {
  return new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Melbourne",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}
function shortTime(value: string): string {
  return new Intl.DateTimeFormat("en-AU", {
    timeZone: "Australia/Melbourne",
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}
function freshness(value: string | null): string {
  if (!value) return "Unavailable";
  const seconds = Math.max(
    0,
    Math.round((Date.now() - new Date(value).valueOf()) / 1_000),
  );
  if (seconds < 5) return "Just now";
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3_600) return `${Math.round(seconds / 60)}m ago`;
  return shortDate(value);
}
function truncateMint(value: string): string {
  return `${value.slice(0, 4)}…${value.slice(-4)}`;
}
function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
function tabLabel(tab: Tab): string {
  return tab === "simulations" ? "Paper trading" : capitalize(tab);
}
function rangeLabel(range: DashboardRange): string {
  return range === "all" ? "All time" : `Last ${range}`;
}
